import { replayEvents } from './reducer.ts';
import type {
  CardInstance,
  ChatMessage,
  ClientGameView,
  ClientSeatView,
  GameEvent,
  GameState,
  PlayerSeat,
  PublicCardView,
  PublicLogEntry,
  SessionType,
} from './types.ts';

export interface ViewerContext {
  accountId: string | null;
  seatId: string | null;
  sessionType: SessionType;
  spectatorCount?: number;
  optInSpectatorChat?: boolean;
  /** Used ONLY on already-delayed overlay snapshots when streamer opted into delayed hand reveal */
  overlayDelayedHandSeatId?: string | null;
}

function toPublicCardView(inst: CardInstance, revealFace: boolean): PublicCardView {
  return {
    instanceId: inst.instanceId,
    ownerSeatId: inst.ownerSeatId,
    controllerSeatId: inst.controllerSeatId,
    zone: inst.zone,
    tapped: inst.tapped,
    faceDown: inst.faceDown,
    flipped: inst.flipped,
    counters: { ...inst.counters },
    attachedToId: inst.attachedToId,
    exiledByInstanceId: inst.exiledByInstanceId,
    row: inst.row,
    freePos: inst.freePos ? { ...inst.freePos } : null,
    isToken: inst.isToken,
    isCommander: inst.isCommander,
    enteredZoneSeq: inst.enteredZoneSeq,
    card: revealFace ? { ...inst.card } : null,
  };
}

/**
 * Determines whether `viewer` may see private cards belonging to `targetSeat`.
 * Enforces:
 * - Eliminated seats become spectators ("no back door")
 * - `table` session in `streamMode` NEVER receives private cards (only `private` session does)
 * - `spectator` and `overlay` NEVER receive live private cards
 */
export function canViewerSeeSeatPrivate(
  state: GameState,
  viewer: ViewerContext,
  targetSeat: PlayerSeat
): boolean {
  // Post-game opt-in hand reveal: once a match is finished, if targetSeat explicitly opted in,
  // viewers may see targetSeat's hand (unless viewing own stream-masked table session).
  if (state.status === 'finished' && targetSeat.revealHandsPostGame) {
    const viewerSeat = viewer.seatId ? state.seats[viewer.seatId] : null;
    const isOwnStreamMasked =
      viewer.sessionType === 'table' &&
      viewerSeat?.seatId === targetSeat.seatId &&
      (viewerSeat.streamMode || state.settings.forceStreamMode);
    if (!isOwnStreamMasked) {
      return true;
    }
  }

  if (!viewer.accountId || !viewer.seatId) return false;
  if (viewer.sessionType === 'spectator') return false;
  if (viewer.sessionType === 'overlay') return false;

  const viewerSeat = state.seats[viewer.seatId];
  if (!viewerSeat || viewerSeat.accountId !== viewer.accountId) return false;
  if (viewerSeat.status === 'eliminated') return false;

  // Stream mode hides private cards from the main `table` session over the wire!
  if (viewer.sessionType === 'table' && (viewerSeat.streamMode || state.settings.forceStreamMode)) {
    return false;
  }

  if (viewerSeat.seatId === targetSeat.seatId) {
    return true;
  }

  // Team hand sharing
  if (
    state.settings.teamMode &&
    state.settings.teammatesSeeHands &&
    viewerSeat.teamId &&
    viewerSeat.teamId === targetSeat.teamId &&
    targetSeat.status !== 'eliminated'
  ) {
    return true;
  }

  return false;
}

function canViewerSeeCardFace(
  state: GameState,
  viewer: ViewerContext,
  inst: CardInstance
): boolean {
  if (!inst.faceDown) return true;

  // Face-down card: only authorized non-stream-masked peekers / controller can see its identity
  if (!viewer.accountId || !viewer.seatId) return false;
  if (viewer.sessionType === 'spectator' || viewer.sessionType === 'overlay') return false;

  const viewerSeat = state.seats[viewer.seatId];
  if (!viewerSeat || viewerSeat.accountId !== viewer.accountId || viewerSeat.status === 'eliminated') {
    return false;
  }
  if (viewer.sessionType === 'table' && (viewerSeat.streamMode || state.settings.forceStreamMode)) {
    return false;
  }

  return (
    inst.controllerSeatId === viewerSeat.seatId ||
    inst.peekedBySeatIds.includes(viewerSeat.seatId)
  );
}

export function sanitizeEventForClient(event: GameEvent): PublicLogEntry {
  const p = event.payload as Record<string, any>;
  let animation: PublicLogEntry['animation'];

  if (event.type === 'CARD_MOVED') {
    animation = {
      kind: 'card_move',
      instanceId: p.instanceId,
      cardName: p.publicCardName ?? null,
      cardImageUrl: p.publicCardImage ?? null,
      fromZone: p.fromZone,
      toZone: p.toZone,
      fromSeatId: p.fromSeatId,
      toSeatId: p.toSeatId,
    };
  } else if (event.type === 'CARDS_DRAWN') {
    animation = {
      kind: 'card_move',
      fromZone: 'library',
      toZone: 'hand',
      fromSeatId: p.seatId,
      toSeatId: p.seatId,
    };
  } else if (event.type === 'CARD_TAPPED') {
    animation = {
      kind: 'tap',
      instanceId: p.instanceId,
    };
  } else if (event.type === 'LIBRARY_SHUFFLED') {
    animation = {
      kind: 'shuffle',
      fromSeatId: p.seatId,
    };
  } else if (event.type === 'LIFE_ADJUSTED') {
    animation = {
      kind: 'life',
      toSeatId: p.targetSeatId,
    };
  } else if (event.type === 'PLAYER_ELIMINATED') {
    animation = {
      kind: 'eliminate',
      toSeatId: p.targetSeatId,
    };
  } else if (event.type === 'FLAG_RAISED') {
    animation = {
      kind: 'flag',
      fromSeatId: p.seatId,
    };
  }

  return {
    seq: event.seq,
    timestamp: event.timestamp,
    turnNumber: event.turnNumber,
    actorSeatId: event.actorSeatId,
    actorName: event.actorName,
    type: event.type,
    summary: event.summary,
    animation,
  };
}

function projectChatForViewer(
  state: GameState,
  viewer: ViewerContext
): ChatMessage[] {
  const viewerSeat = viewer.seatId ? state.seats[viewer.seatId] : null;
  const isEliminatedOrSpectator =
    viewer.sessionType === 'spectator' ||
    viewer.sessionType === 'overlay' ||
    !viewerSeat ||
    viewerSeat.status === 'eliminated';

  const result: ChatMessage[] = [];
  for (const msg of state.chat) {
    if (msg.channel === 'spectator' && !isEliminatedOrSpectator && !viewer.optInSpectatorChat) {
      continue;
    }
    if (msg.whisperToSeatId) {
      const targetName = state.seats[msg.whisperToSeatId]?.displayName ?? msg.whisperToSeatId;
      const isParticipant =
        !isEliminatedOrSpectator &&
        viewer.sessionType === 'private'
          ? viewerSeat?.seatId === msg.seatId || viewerSeat?.seatId === msg.whisperToSeatId
          : !viewerSeat?.streamMode &&
            (viewerSeat?.seatId === msg.seatId || viewerSeat?.seatId === msg.whisperToSeatId);
      if (!isParticipant) {
        result.push({
          ...msg,
          text: `[Whispered to ${targetName}: ${'•'.repeat(Math.min(24, Math.max(6, msg.text.length)))}]`,
        });
        continue;
      }
    }
    result.push({ ...msg });
  }
  return result;
}

/**
 * Projects authoritative `GameState` into a zero-leak `ClientGameView` for a specific socket.
 */
export function projectGameView(
  state: GameState,
  eventLog: readonly GameEvent[],
  viewer: ViewerContext
): ClientGameView {
  const viewerSeat = viewer.seatId ? state.seats[viewer.seatId] : null;
  const effectiveViewerSeatId =
    viewerSeat && viewerSeat.accountId === viewer.accountId && viewerSeat.status !== 'eliminated'
      ? viewerSeat.seatId
      : null;
  const effectiveSessionType: SessionType =
    viewerSeat?.status === 'eliminated' ? 'spectator' : viewer.sessionType;

  const normalizedViewer: ViewerContext = {
    ...viewer,
    seatId: effectiveViewerSeatId,
    sessionType: effectiveSessionType,
  };

  const seatsView: Record<string, ClientSeatView> = {};
  const grantedLibraryViews: ClientGameView['grantedLibraryViews'] = [];
  const viewerCanSeeOwnPrivate =
    effectiveViewerSeatId && state.seats[effectiveViewerSeatId]
      ? canViewerSeeSeatPrivate(state, normalizedViewer, state.seats[effectiveViewerSeatId])
      : false;

  for (const sid of state.seatOrder) {
    const seat = state.seats[sid];
    if (!seat) continue;
    const z = state.zoneOrder[sid];
    const canSeePrivate = canViewerSeeSeatPrivate(state, normalizedViewer, seat);
    const allowOverlayDelayedHand =
      normalizedViewer.sessionType === 'overlay' &&
      normalizedViewer.overlayDelayedHandSeatId === sid;

    const handCards =
      canSeePrivate || allowOverlayDelayedHand
        ? z.hand
            .map((cid) => state.cards[cid])
            .filter(Boolean)
            .map((inst) => toPublicCardView(inst, true))
        : null;

    const topLibInst =
      seat.playWithTopRevealed && z.library.length > 0 ? state.cards[z.library[0]] : null;

    const privateLookCards =
      canSeePrivate && seat.privateLook && seat.seatId === effectiveViewerSeatId
        ? seat.privateLook.cardInstanceIds
            .map((cid) => state.cards[cid])
            .filter(Boolean)
            .map((inst) => toPublicCardView(inst, true))
        : null;

    const sideboardCards =
      canSeePrivate && !state.settings.competitiveMode && seat.seatId === effectiveViewerSeatId
        ? z.sideboard
            .map((cid) => state.cards[cid])
            .filter(Boolean)
            .map((inst) => toPublicCardView(inst, true))
        : null;

    // Deck transparency respects player consent, stream mode, and competitive mode
    let projectedDeckMeta = seat.deckMeta ? structuredClone(seat.deckMeta) : null;
    if (projectedDeckMeta && (!canSeePrivate || seat.seatId !== effectiveViewerSeatId)) {
      if (!projectedDeckMeta.showTransparency && seat.seatId !== effectiveViewerSeatId) {
        projectedDeckMeta = {
          ...projectedDeckMeta,
          name: 'Hidden Deck',
          commanderNames: [],
          legalCosmeticArt: [],
        };
      } else {
        // Opponents and stream-masked sockets never receive the full maindeck art pool over the wire
        projectedDeckMeta = {
          ...projectedDeckMeta,
          legalCosmeticArt: [],
        };
      }
    }

    if (
      viewerCanSeeOwnPrivate &&
      effectiveViewerSeatId &&
      seat.grantedLibraryAccess &&
      seat.grantedLibraryAccess.granteeSeatId === effectiveViewerSeatId
    ) {
      grantedLibraryViews.push({
        ownerSeatId: seat.seatId,
        ownerName: seat.displayName,
        scope: seat.grantedLibraryAccess.scope,
        takeFaceDown: seat.grantedLibraryAccess.takeFaceDown,
        cards: seat.grantedLibraryAccess.cardInstanceIds
          .map((cid) => state.cards[cid])
          .filter((inst): inst is CardInstance => Boolean(inst && inst.zone === 'library'))
          .map((inst) => toPublicCardView(inst, true)),
      });
    }

    seatsView[sid] = {
      seatId: seat.seatId,
      seatIndex: seat.seatIndex,
      publicId: seat.publicId,
      displayName: seat.displayName,
      avatarUrl: state.settings.forceDefaultCosmetics ? null : seat.avatarUrl,
      profileColor: seat.profileColor,
      matBgCss: seat.matBgCss,
      matBorderCss: seat.matBorderCss,
      verifiedProvider: seat.verifiedProvider,
      teamId: seat.teamId,
      status: seat.status,
      connected: seat.connected,
      awaySince: seat.awaySince,
      streamMode: seat.streamMode || state.settings.forceStreamMode,
      sleeveUrl: state.settings.forceDefaultCosmetics ? null : seat.sleeveUrl,
      sleeveCardName: state.settings.forceDefaultCosmetics ? null : seat.sleeveCardName,
      playmatUrl: state.settings.forceDefaultCosmetics ? null : seat.playmatUrl,
      playmatCardName: state.settings.forceDefaultCosmetics ? null : seat.playmatCardName,
      playmatFocalPoint: state.settings.forceDefaultCosmetics ? 'center' : (seat.playmatFocalPoint ?? 'center'),
      life: seat.life,
      startingLife: seat.startingLife,
      playerCounters: { ...seat.playerCounters },
      manaPool: { ...seat.manaPool },
      privateNotes: canSeePrivate && seat.seatId === effectiveViewerSeatId ? seat.privateNotes : null,
      savedForLater: canSeePrivate && seat.seatId === effectiveViewerSeatId ? [...seat.savedForLater] : null,
      commanderDamage: { ...seat.commanderDamage },
      commanderTax: { ...seat.commanderTax },
      mulliganCount: seat.mulliganCount,
      handConfirmed: seat.handConfirmed,
      playWithTopRevealed: seat.playWithTopRevealed,
      revealHandsPostGame: Boolean(seat.revealHandsPostGame),
      stopPresets: seat.seatId === effectiveViewerSeatId ? [...seat.stopPresets] : [],
      flagMuted: seat.flagMuted,
      hasGrantedLibraryAccessTo: seat.grantedLibraryAccess
        ? {
            granteeSeatId: seat.grantedLibraryAccess.granteeSeatId,
            scope: seat.grantedLibraryAccess.scope,
            count: seat.grantedLibraryAccess.count,
            takeFaceDown: seat.grantedLibraryAccess.takeFaceDown,
          }
        : null,
      deckMeta: projectedDeckMeta,
      availableTokens: seat.seatId === effectiveViewerSeatId ? [...seat.availableTokens] : [],
      paperMode: {
        ...seat.paperMode,
        tableOnlyCrop: { ...seat.paperMode.tableOnlyCrop },
        videoMarkers: seat.paperMode.videoMarkers.map((m) => ({ ...m })),
        scannedCardsSeen: seat.paperMode.scannedCardsSeen.map((s) => ({ ...s })),
        registeredAlters:
          canSeePrivate && seat.seatId === effectiveViewerSeatId
            ? seat.paperMode.registeredAlters.map((a) => ({ ...a }))
            : [],
      },
      eliminatedInfo: seat.eliminatedInfo ? { ...seat.eliminatedInfo } : null,
      zones: {
        libraryCount: z.library.length,
        libraryTopCard: topLibInst ? toPublicCardView(topLibInst, true) : null,
        handCount: z.hand.length,
        handCards,
        battlefield: z.battlefield
          .map((cid) => state.cards[cid])
          .filter(Boolean)
          .map((inst) => toPublicCardView(inst, canViewerSeeCardFace(state, normalizedViewer, inst))),
        graveyard: z.graveyard
          .map((cid) => state.cards[cid])
          .filter(Boolean)
          .map((inst) => toPublicCardView(inst, true)),
        exile: z.exile
          .map((cid) => state.cards[cid])
          .filter(Boolean)
          .map((inst) => toPublicCardView(inst, canViewerSeeCardFace(state, normalizedViewer, inst))),
        command: z.command
          .map((cid) => state.cards[cid])
          .filter(Boolean)
          .map((inst) => toPublicCardView(inst, true)),
        sideboardCount: z.sideboard.length,
        sideboardCards,
      },
      activePrivateAction: seat.privateLook
        ? { kind: seat.privateLook.kind, count: seat.privateLook.count }
        : null,
      privateLookCards,
    };
  }

  // Consolidated view removes eliminated seats from activeSeatOrder
  const activeSeatOrder = state.seatOrder.filter((sid) => {
    const s = state.seats[sid];
    return s && s.status !== 'eliminated' && (state.status === 'lobby' || s.accountId !== null);
  });

  const recentLog = eventLog.slice(-120).map(sanitizeEventForClient);

  return {
    tableId: state.tableId,
    inviteCode: state.inviteCode,
    isHost: Boolean(viewer.accountId && viewer.accountId === state.hostAccountId),
    isJudge: Boolean(viewer.accountId && state.judgeAccountIds.includes(viewer.accountId)),
    viewerSeatId: effectiveViewerSeatId,
    viewerSessionType: effectiveSessionType,
    locked: state.locked,
    status: state.status,
    settings: { ...state.settings },
    lateJoinRequests: (state.lateJoinRequests ?? []).map(({ accountId: _omit, ...rest }) => ({ ...rest })),
    seatOrder: [...state.seatOrder],
    activeSeatOrder,
    turnDirection: state.turnDirection,
    activeSeatId: state.activeSeatId,
    phase: state.phase,
    turnNumber: state.turnNumber,
    extraTurnsQueue: [...state.extraTurnsQueue],
    skipTurnSeatIds: [...state.skipTurnSeatIds],
    pregame: structuredClone(state.pregame),
    grantedLibraryViews,
    canUndoPassTurn: Boolean(state.lastTurnSnapshot),
    seats: seatsView,
    sharedZones: {
      stack: state.sharedZones.stack
        .map((cid) => state.cards[cid])
        .filter(Boolean)
        .map((inst) => toPublicCardView(inst, canViewerSeeCardFace(state, normalizedViewer, inst))),
      revealed: state.sharedZones.revealed
        .map((cid) => state.cards[cid])
        .filter(Boolean)
        .map((inst) => toPublicCardView(inst, true)),
    },
    flags: state.flags.map((f) => ({ ...f })),
    designations: structuredClone(state.designations),
    sharedNotes: state.sharedNotes,
    chat: projectChatForViewer(state, normalizedViewer),
    spectatorCount: viewer.spectatorCount ?? 0,
    winnerSeatIds: [...state.winnerSeatIds],
    recentLog,
    seq: state.seq,
  };
}

/**
 * Scrubbable replay frame projection at `stepCount` (`0..allEvents.length`).
 * Preserves all original hidden-information boundaries (libraries, unpeeked face-down cards,
 * private notes, and opponents' hands) unless a seat's owner explicitly opted into post-game hand reveal!
 */
export function projectReplayFrameAtStep(
  initialState: GameState,
  allEvents: readonly GameEvent[],
  stepCount: number,
  viewer: ViewerContext,
  postGameRevealSeatIds?: ReadonlySet<string>
): ClientGameView {
  const clamped = Math.max(0, Math.min(allEvents.length, Math.floor(stepCount)));
  const slice = allEvents.slice(0, clamped);
  const historicalState = replayEvents(initialState, slice);

  const view = projectGameView(historicalState, slice, viewer);

  if (postGameRevealSeatIds && postGameRevealSeatIds.size > 0) {
    const viewerSeat = viewer.seatId ? historicalState.seats[viewer.seatId] : null;
    for (const sid of postGameRevealSeatIds) {
      const seat = historicalState.seats[sid];
      const seatView = view.seats[sid];
      if (!seat || !seatView) continue;
      const isOwnStreamMasked =
        viewer.sessionType === 'table' &&
        viewerSeat?.seatId === sid &&
        (viewerSeat.streamMode || historicalState.settings.forceStreamMode);
      if (isOwnStreamMasked) continue;

      seatView.revealHandsPostGame = true;
      seatView.zones.handCards = (historicalState.zoneOrder[sid]?.hand ?? [])
        .map((cid) => historicalState.cards[cid])
        .filter(Boolean)
        .map((inst) => toPublicCardView(inst, true));
    }
  }

  return view;
}
